import * as THREE from "three";
import { COSMETICS, unitById } from "@zend/content";
import { applyCosmeticPaint } from "../assets/cosmeticPaint.js";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import type { MarchMember } from "./types.js";

export type MarchModelState = "loading" | "ready" | "partial" | "error" | "legacy";
export const MARCH_MODEL_LIMIT = 3;
type Actor = { instance: ModelInstance; restore: () => void };

/** Three real members form a readable column; the selection card lists the complete persisted squad. */
export class MarchColumn {
  readonly group = new THREE.Group();
  private readonly models = new THREE.Group();
  private readonly direction: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  private actors: Actor[] = [];
  private members: MarchMember[] = [];
  private token = 0;
  private disposed = false;
  private signature = "";
  private moving = true;

  constructor(private readonly onState: (state: MarchModelState) => void = () => undefined) {
    this.group.name = "march-column";
    this.models.name = "march-unit-models";
    const arrow = new THREE.Shape();
    arrow.moveTo(-0.22, -0.12); arrow.lineTo(0, 0.2); arrow.lineTo(0.22, -0.12); arrow.lineTo(0, -0.02); arrow.closePath();
    // The shape's +Y tip must become the column's +Z travel direction.
    const geometry = new THREE.ShapeGeometry(arrow); geometry.rotateX(Math.PI / 2);
    this.direction = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xffe3a0, side: THREE.DoubleSide, depthWrite: false }));
    this.direction.name = "march-direction"; this.direction.position.set(0, 0.08, 1.02);
    this.halo = new THREE.Mesh(new THREE.RingGeometry(0.83, 0.9, 48), new THREE.MeshBasicMaterial({ color: 0xffd887, side: THREE.DoubleSide, transparent: true, opacity: 0.8, depthWrite: false }));
    this.halo.rotation.x = -Math.PI / 2; this.halo.position.y = 0.065; this.halo.name = "march-selection";
    this.group.add(this.halo, this.direction, this.models);
  }

  setMembers(members: MarchMember[]): void {
    const signature = JSON.stringify(members.map(({ rowId, unitId, skin }) => [rowId, unitId, skin]));
    if (signature === this.signature) return;
    this.signature = signature; this.members = members.map((member) => ({ ...member })); this.load();
  }

  private state(value: MarchModelState): void { this.group.userData["state"] = value; this.onState(value); }
  retry(): void { if (!this.disposed) this.load(); }
  private clearActors(): void {
    for (const actor of this.actors) { actor.restore(); actor.instance.mixer.stopAllAction(); actor.instance.mixer.uncacheRoot(actor.instance.object); }
    this.actors = []; this.models.clear();
  }

  private load(): void {
    const token = ++this.token; this.clearActors();
    if (!this.members.length) { this.state("legacy"); return; }
    this.state("loading");
    const members = this.members.slice(0, MARCH_MODEL_LIMIT);
    let settled = 0;
    const finish = () => { if (++settled === members.length) this.state(this.actors.length === members.length ? "ready" : this.actors.length ? "partial" : "error"); };
    members.forEach((member, index) => {
      void loadModel(member.unitId, undefined, 0).then((model) => {
        if (this.disposed || token !== this.token) return;
        const instance = instantiate(model);
        // Shipped tanks face +X; other chassis face +Z after Blender's Y-up export.
        // Rotate only this clone, before calculating its formation footprint.
        if (unitById(member.unitId).chassis === "tank") instance.object.rotation.y = -Math.PI / 2;
        instance.object.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(instance.object), size = box.getSize(new THREE.Vector3()), centre = box.getCenter(new THREE.Vector3());
        const scale = Math.min(1.35 / Math.max(0.1, size.y), 0.85 / Math.max(0.1, size.x, size.z));
        instance.object.scale.setScalar(scale);
        instance.object.position.set((index === 0 ? 0 : index === 1 ? -0.48 : 0.48) - centre.x * scale, -box.min.y * scale + 0.07, (index === 0 ? 0.35 : -0.45) - centre.z * scale);
        instance.object.userData["rowId"] = member.rowId; instance.object.userData["assetId"] = member.unitId;
        instance.object.userData["modelUrl"] = model.url; instance.object.name = `march-unit:${member.rowId}`;
        const cosmetic = COSMETICS.find((candidate) => candidate.id === member.skin && candidate.target === member.unitId);
        const restore = cosmetic ? applyCosmeticPaint(instance.object, { ...cosmetic, colour: cosmetic.colour ?? null }) : () => undefined;
        instance.play(this.moving && instance.actions["walk"] ? "walk" : "idle");
        this.actors.push({ instance, restore }); this.models.add(instance.object); finish();
      }).catch(() => { if (!this.disposed && token === this.token) finish(); });
    });
  }

  update(from: THREE.Vector3, to: THREE.Vector3, progress: number, dt: number, reducedMotion: boolean, selected: boolean): void {
    if (this.disposed) return;
    this.group.position.lerpVectors(from, to, progress);
    const dx = to.x - from.x, dz = to.z - from.z;
    if (dx * dx + dz * dz > 0.0001) this.group.rotation.y = Math.atan2(dx, dz);
    const moving = progress < 1 && dx * dx + dz * dz > 0.0001;
    if (moving !== this.moving) { this.moving = moving; for (const { instance } of this.actors) instance.play(moving && instance.actions["walk"] ? "walk" : "idle"); }
    this.halo.scale.setScalar(selected ? 1.12 : 1); (this.halo.material as THREE.MeshBasicMaterial).opacity = selected ? 1 : 0.45;
    this.direction.visible = moving; this.direction.position.z = 1.02;
    for (const { instance } of this.actors) if (!reducedMotion) instance.mixer.update(Math.min(dt, 0.1));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; ++this.token; this.clearActors();
    for (const mesh of [this.direction, this.halo]) { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); }
    this.group.removeFromParent(); this.group.clear();
  }
}
